import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BunnyLoader from '../components/ui/BunnyLoader';
import { Button } from '../components/ui/button';
import { hirelaneApi } from '../services/api';
import './HirelaneDashboard.css';

function formatUpdatedAt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return null;
  }
}

function statusBadgeClass(status) {
  if (status === 'Open') return 'hirelane-dash__status hirelane-dash__status--open';
  if (status === 'Closed') return 'hirelane-dash__status hirelane-dash__status--closed';
  return 'hirelane-dash__status hirelane-dash__status--draft';
}

export default function HirelaneDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');

  const [vendorsModalOpen, setVendorsModalOpen] = useState(false);
  const [vendorsList, setVendorsList] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorsError, setVendorsError] = useState(null);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorNotes, setNewVendorNotes] = useState('');
  const [vendorSaving, setVendorSaving] = useState(false);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [editVendorName, setEditVendorName] = useState('');
  const [editVendorNotes, setEditVendorNotes] = useState('');

  const [interviewersModalOpen, setInterviewersModalOpen] = useState(false);
  const [interviewersList, setInterviewersList] = useState([]);
  const [interviewersLoading, setInterviewersLoading] = useState(false);
  const [interviewersError, setInterviewersError] = useState(null);
  const [newInterviewerName, setNewInterviewerName] = useState('');
  const [newInterviewerEmail, setNewInterviewerEmail] = useState('');
  const [newInterviewerNotes, setNewInterviewerNotes] = useState('');
  const [interviewerSaving, setInterviewerSaving] = useState(false);
  const [editingInterviewerId, setEditingInterviewerId] = useState(null);
  const [editInterviewerName, setEditInterviewerName] = useState('');
  const [editInterviewerEmail, setEditInterviewerEmail] = useState('');
  const [editInterviewerNotes, setEditInterviewerNotes] = useState('');

  const params = useMemo(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (status !== 'all') p.status = status;
    return p;
  }, [q, status]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await hirelaneApi.listJobs(params);
        if (!cancelled) setJobs(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load jobs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params]);

  const stats = useMemo(() => {
    const list = jobs;
    return {
      total: list.length,
      open: list.filter((j) => j.status === 'Open').length,
      draft: list.filter((j) => j.status === 'Draft').length,
      closed: list.filter((j) => j.status === 'Closed').length,
      candidates: list.reduce((n, j) => n + (Number(j.candidate_count) || 0), 0)
    };
  }, [jobs]);

  const loadVendors = async () => {
    try {
      setVendorsLoading(true);
      setVendorsError(null);
      const data = await hirelaneApi.listVendors();
      setVendorsList(Array.isArray(data) ? data : []);
    } catch (e) {
      setVendorsError(e?.response?.data?.error || e?.message || 'Failed to load vendors');
    } finally {
      setVendorsLoading(false);
    }
  };

  const openVendorsModal = () => {
    setVendorsModalOpen(true);
    setEditingVendorId(null);
    setNewVendorName('');
    setNewVendorNotes('');
    loadVendors();
  };

  const addVendor = async () => {
    const name = newVendorName.trim();
    if (!name) return;
    try {
      setVendorSaving(true);
      setVendorsError(null);
      await hirelaneApi.createVendor({ name, notes: newVendorNotes.trim() || undefined });
      setNewVendorName('');
      setNewVendorNotes('');
      await loadVendors();
    } catch (e) {
      setVendorsError(e?.response?.data?.error || e?.message || 'Failed to add vendor');
    } finally {
      setVendorSaving(false);
    }
  };

  const startEditVendor = (v) => {
    setEditingVendorId(v.id);
    setEditVendorName(v.name || '');
    setEditVendorNotes(v.notes || '');
  };

  const cancelEditVendor = () => {
    setEditingVendorId(null);
  };

  const saveEditVendor = async () => {
    if (!editingVendorId) return;
    const name = editVendorName.trim();
    if (!name) return;
    try {
      setVendorSaving(true);
      setVendorsError(null);
      await hirelaneApi.updateVendor(editingVendorId, {
        name,
        notes: editVendorNotes.trim() || null
      });
      setEditingVendorId(null);
      await loadVendors();
    } catch (e) {
      setVendorsError(e?.response?.data?.error || e?.message || 'Failed to update vendor');
    } finally {
      setVendorSaving(false);
    }
  };

  const deleteVendor = async (v) => {
    if (!window.confirm(`Remove vendor “${v.name}”?`)) return;
    try {
      setVendorSaving(true);
      setVendorsError(null);
      await hirelaneApi.deleteVendor(v.id);
      if (editingVendorId === v.id) setEditingVendorId(null);
      await loadVendors();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to delete';
      setVendorsError(msg);
    } finally {
      setVendorSaving(false);
    }
  };

  const loadInterviewers = async () => {
    try {
      setInterviewersLoading(true);
      setInterviewersError(null);
      const data = await hirelaneApi.listInterviewers();
      setInterviewersList(Array.isArray(data) ? data : []);
    } catch (e) {
      setInterviewersError(e?.response?.data?.error || e?.message || 'Failed to load interviewers');
    } finally {
      setInterviewersLoading(false);
    }
  };

  const openInterviewersModal = () => {
    setInterviewersModalOpen(true);
    setEditingInterviewerId(null);
    setNewInterviewerName('');
    setNewInterviewerEmail('');
    setNewInterviewerNotes('');
    loadInterviewers();
  };

  const addInterviewer = async () => {
    const display_name = newInterviewerName.trim();
    const email = newInterviewerEmail.trim();
    if (!display_name || !email) return;
    try {
      setInterviewerSaving(true);
      setInterviewersError(null);
      await hirelaneApi.createInterviewer({
        display_name,
        email,
        notes: newInterviewerNotes.trim() || undefined
      });
      setNewInterviewerName('');
      setNewInterviewerEmail('');
      setNewInterviewerNotes('');
      await loadInterviewers();
    } catch (e) {
      setInterviewersError(e?.response?.data?.error || e?.message || 'Failed to add interviewer');
    } finally {
      setInterviewerSaving(false);
    }
  };

  const startEditInterviewer = (row) => {
    setEditingInterviewerId(row.id);
    setEditInterviewerName(row.display_name || '');
    setEditInterviewerEmail(row.email || '');
    setEditInterviewerNotes(row.notes || '');
  };

  const cancelEditInterviewer = () => {
    setEditingInterviewerId(null);
  };

  const saveEditInterviewer = async () => {
    if (!editingInterviewerId) return;
    const display_name = editInterviewerName.trim();
    const email = editInterviewerEmail.trim();
    if (!display_name || !email) return;
    try {
      setInterviewerSaving(true);
      setInterviewersError(null);
      await hirelaneApi.updateInterviewer(editingInterviewerId, {
        display_name,
        email,
        notes: editInterviewerNotes.trim() || null
      });
      setEditingInterviewerId(null);
      await loadInterviewers();
    } catch (e) {
      setInterviewersError(e?.response?.data?.error || e?.message || 'Failed to update interviewer');
    } finally {
      setInterviewerSaving(false);
    }
  };

  const deleteInterviewer = async (row) => {
    if (!window.confirm(`Remove interviewer “${row.display_name}”?`)) return;
    try {
      setInterviewerSaving(true);
      setInterviewersError(null);
      await hirelaneApi.deleteInterviewer(row.id);
      if (editingInterviewerId === row.id) setEditingInterviewerId(null);
      await loadInterviewers();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to delete';
      setInterviewersError(msg);
    } finally {
      setInterviewerSaving(false);
    }
  };

  if (loading) return <BunnyLoader />;

  return (
    <div className="hirelane-dash">
      <header className="hirelane-dash__hero">
        <div className="hirelane-dash__heroInner">
          <div>
            <p className="hirelane-dash__kicker">Hirelane</p>
            <h1 className="hirelane-dash__title">Hiring dashboard</h1>
            <p className="hirelane-dash__subtitle">
              Track roles, candidates, and interviews in one place. Open a job to work the pipeline or edit posting
              details anytime.
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <Button variant="secondary" onClick={openVendorsModal}>
              Manage vendors
            </Button>
            <Button variant="secondary" onClick={openInterviewersModal}>
              Manage interviewers
            </Button>
            <Button onClick={() => navigate('/hirelane/jobs/new')}>Create job</Button>
          </div>
        </div>

        <div className="hirelane-dash__stats" aria-label="Summary counts">
          <div className="hirelane-dash__stat">
            <div className="hirelane-dash__statValue">{stats.total}</div>
            <div className="hirelane-dash__statLabel">Jobs</div>
          </div>
          <div className="hirelane-dash__stat">
            <div className="hirelane-dash__statValue">{stats.open}</div>
            <div className="hirelane-dash__statLabel">Open</div>
          </div>
          <div className="hirelane-dash__stat">
            <div className="hirelane-dash__statValue">{stats.draft}</div>
            <div className="hirelane-dash__statLabel">Draft</div>
          </div>
          <div className="hirelane-dash__stat">
            <div className="hirelane-dash__statValue">{stats.closed}</div>
            <div className="hirelane-dash__statLabel">Closed</div>
          </div>
          <div className="hirelane-dash__stat">
            <div className="hirelane-dash__statValue">{stats.candidates}</div>
            <div className="hirelane-dash__statLabel">Candidates</div>
          </div>
        </div>
      </header>

      <div className="hirelane-dash__toolbar">
        <input
          className="hirelane-dash__search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title, department, or location…"
          aria-label="Search jobs"
          type="search"
        />
        <select
          className="hirelane-dash__filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="Draft">Draft</option>
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
        </select>
        <Button type="button" variant="secondary" className="hirelane-dash__toolbarVendors" onClick={openVendorsModal}>
          Manage vendors
        </Button>
        <Button type="button" variant="secondary" onClick={openInterviewersModal}>
          Manage interviewers
        </Button>
      </div>

      {error && <div className="hirelane-dash__alert">{error}</div>}

      {jobs.length === 0 ? (
        <div className="hirelane-dash__empty">
          <p className="hirelane-dash__emptyTitle">No jobs match your filters</p>
          <p style={{ margin: '0 0 16px', fontSize: 14 }}>
            Try clearing search or choose “All statuses”. When you are ready, create your first role.
          </p>
          <Button onClick={() => navigate('/hirelane/jobs/new')}>Create job</Button>
        </div>
      ) : (
        <div className="hirelane-dash__grid">
          {jobs.map((j) => {
            const openPipeline = () => navigate(`/hirelane/jobs/${j.id}`);
            const openEdit = (e) => {
              e.stopPropagation();
              navigate(`/hirelane/jobs/${j.id}/edit`);
            };
            const updated = formatUpdatedAt(j.updated_at);
            return (
              <article key={j.id} className="hirelane-dash__card">
                <div
                  className="hirelane-dash__cardBody"
                  role="button"
                  tabIndex={0}
                  onClick={openPipeline}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openPipeline();
                    }
                  }}
                >
                  <div className="hirelane-dash__cardTitleRow">
                    <h2 className="hirelane-dash__cardTitle">{j.title}</h2>
                    <span className={statusBadgeClass(j.status)}>{j.status || 'Draft'}</span>
                  </div>
                  <div className="hirelane-dash__meta">
                    <span className="hirelane-dash__metaStrong">{j.department?.trim() || 'Department not set'}</span>
                    {j.location ? (
                      <>
                        {' · '}
                        {j.location}
                      </>
                    ) : null}
                  </div>
                  <div className="hirelane-dash__metrics">
                    <span title="Total candidates on this job">
                      <strong>{j.candidate_count ?? 0}</strong> candidates
                    </span>
                    <span title="Active pipeline (not rejected)">
                      <strong>{j.pending_count ?? 0}</strong> in pipeline
                    </span>
                    <span title="Shortlisted">
                      <strong>{j.shortlisted_count ?? 0}</strong> shortlisted
                    </span>
                  </div>
                  {updated && (
                    <div className="hirelane-dash__updated">Updated {updated}</div>
                  )}
                </div>
                <div className="hirelane-dash__cardActions">
                  <Button
                    type="button"
                    variant="secondary"
                    className="hirelane-dash__actionBtn"
                    onClick={openEdit}
                  >
                    Edit job
                  </Button>
                  <Button type="button" className="hirelane-dash__actionBtn" onClick={(e) => {
                    e.stopPropagation();
                    openPipeline();
                  }}>
                    Open pipeline
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {vendorsModalOpen ? (
        <>
          <div
            className="hirelane-dash__modalBackdrop"
            role="presentation"
            onClick={() => setVendorsModalOpen(false)}
            aria-hidden
          />
          <div className="hirelane-dash__modal" role="dialog" aria-modal="true" aria-labelledby="hirelane-vendors-title">
            <div className="hirelane-dash__modalHeader">
              <h2 id="hirelane-vendors-title" className="hirelane-dash__modalTitle">
                Vendors (all jobs)
              </h2>
              <button
                type="button"
                className="hirelane-dash__modalClose"
                onClick={() => setVendorsModalOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="hirelane-dash__modalIntro">
              Agencies and vendors are shared across every Hirelane job. When you add a candidate, you can link their
              profile to one of these vendors or mark a referral instead.
            </p>
            {vendorsError ? <div className="hirelane-dash__alert hirelane-dash__alert--modal">{vendorsError}</div> : null}
            <div className="hirelane-dash__vendorAdd">
              <input
                className="hirelane-dash__vendorInput"
                value={newVendorName}
                onChange={(e) => setNewVendorName(e.target.value)}
                placeholder="Vendor name"
                aria-label="New vendor name"
              />
              <input
                className="hirelane-dash__vendorInput"
                value={newVendorNotes}
                onChange={(e) => setNewVendorNotes(e.target.value)}
                placeholder="Notes (optional)"
                aria-label="New vendor notes"
              />
              <Button type="button" onClick={addVendor} disabled={vendorSaving || !newVendorName.trim()}>
                Add vendor
              </Button>
            </div>
            {vendorsLoading ? (
              <p className="hirelane-dash__vendorLoading">Loading…</p>
            ) : vendorsList.length === 0 ? (
              <p className="hirelane-dash__vendorEmpty">No vendors yet. Add one above.</p>
            ) : (
              <ul className="hirelane-dash__vendorList">
                {vendorsList.map((v) => (
                  <li key={v.id} className="hirelane-dash__vendorRow">
                    {editingVendorId === v.id ? (
                      <div className="hirelane-dash__vendorEdit">
                        <input
                          className="hirelane-dash__vendorInput"
                          value={editVendorName}
                          onChange={(e) => setEditVendorName(e.target.value)}
                          aria-label="Edit vendor name"
                        />
                        <input
                          className="hirelane-dash__vendorInput"
                          value={editVendorNotes}
                          onChange={(e) => setEditVendorNotes(e.target.value)}
                          placeholder="Notes"
                          aria-label="Edit vendor notes"
                        />
                        <div className="hirelane-dash__vendorEditActions">
                          <Button type="button" variant="secondary" onClick={cancelEditVendor} disabled={vendorSaving}>
                            Cancel
                          </Button>
                          <Button type="button" onClick={saveEditVendor} disabled={vendorSaving || !editVendorName.trim()}>
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="hirelane-dash__vendorMain">
                          <span className="hirelane-dash__vendorName">{v.name}</span>
                          {v.notes ? <span className="hirelane-dash__vendorNotes">{v.notes}</span> : null}
                        </div>
                        <div className="hirelane-dash__vendorActions">
                          <Button type="button" variant="secondary" onClick={() => startEditVendor(v)} disabled={vendorSaving}>
                            Edit
                          </Button>
                          <Button type="button" variant="secondary" onClick={() => deleteVendor(v)} disabled={vendorSaving}>
                            Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}

      {interviewersModalOpen ? (
        <>
          <div
            className="hirelane-dash__modalBackdrop"
            role="presentation"
            onClick={() => setInterviewersModalOpen(false)}
            aria-hidden
          />
          <div
            className="hirelane-dash__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hirelane-interviewers-title"
          >
            <div className="hirelane-dash__modalHeader">
              <h2 id="hirelane-interviewers-title" className="hirelane-dash__modalTitle">
                Interviewers (all jobs)
              </h2>
              <button
                type="button"
                className="hirelane-dash__modalClose"
                onClick={() => setInterviewersModalOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="hirelane-dash__modalIntro">
              Save people you interview with often. When you schedule an interview on a job, you can pick several from
              this list or add one-off guests with name and email.
            </p>
            {interviewersError ? (
              <div className="hirelane-dash__alert hirelane-dash__alert--modal">{interviewersError}</div>
            ) : null}
            <div className="hirelane-dash__vendorAdd">
              <input
                className="hirelane-dash__vendorInput"
                value={newInterviewerName}
                onChange={(e) => setNewInterviewerName(e.target.value)}
                placeholder="Display name"
                aria-label="New interviewer name"
              />
              <input
                className="hirelane-dash__vendorInput"
                type="email"
                value={newInterviewerEmail}
                onChange={(e) => setNewInterviewerEmail(e.target.value)}
                placeholder="Email (for calendar invites)"
                aria-label="New interviewer email"
              />
              <input
                className="hirelane-dash__vendorInput"
                value={newInterviewerNotes}
                onChange={(e) => setNewInterviewerNotes(e.target.value)}
                placeholder="Notes (optional)"
                aria-label="New interviewer notes"
              />
              <Button
                type="button"
                onClick={addInterviewer}
                disabled={
                  interviewerSaving || !newInterviewerName.trim() || !newInterviewerEmail.trim()
                }
              >
                Add interviewer
              </Button>
            </div>
            {interviewersLoading ? (
              <p className="hirelane-dash__vendorLoading">Loading…</p>
            ) : interviewersList.length === 0 ? (
              <p className="hirelane-dash__vendorEmpty">No saved interviewers yet. Add one above.</p>
            ) : (
              <ul className="hirelane-dash__vendorList">
                {interviewersList.map((row) => (
                  <li key={row.id} className="hirelane-dash__vendorRow">
                    {editingInterviewerId === row.id ? (
                      <div className="hirelane-dash__vendorEdit">
                        <input
                          className="hirelane-dash__vendorInput"
                          value={editInterviewerName}
                          onChange={(e) => setEditInterviewerName(e.target.value)}
                          aria-label="Edit interviewer name"
                        />
                        <input
                          className="hirelane-dash__vendorInput"
                          type="email"
                          value={editInterviewerEmail}
                          onChange={(e) => setEditInterviewerEmail(e.target.value)}
                          aria-label="Edit interviewer email"
                        />
                        <input
                          className="hirelane-dash__vendorInput"
                          value={editInterviewerNotes}
                          onChange={(e) => setEditInterviewerNotes(e.target.value)}
                          placeholder="Notes"
                          aria-label="Edit interviewer notes"
                        />
                        <div className="hirelane-dash__vendorEditActions">
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={cancelEditInterviewer}
                            disabled={interviewerSaving}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            onClick={saveEditInterviewer}
                            disabled={
                              interviewerSaving ||
                              !editInterviewerName.trim() ||
                              !editInterviewerEmail.trim()
                            }
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="hirelane-dash__vendorMain">
                          <span className="hirelane-dash__vendorName">{row.display_name}</span>
                          <span className="hirelane-dash__vendorNotes" style={{ display: 'block' }}>
                            {row.email}
                          </span>
                          {row.notes ? <span className="hirelane-dash__vendorNotes">{row.notes}</span> : null}
                        </div>
                        <div className="hirelane-dash__vendorActions">
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => startEditInterviewer(row)}
                            disabled={interviewerSaving}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => deleteInterviewer(row)}
                            disabled={interviewerSaving}
                          >
                            Delete
                          </Button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
